import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';
import delay from 'lodash/delay';
import throttle from 'lodash/throttle';
import axios from 'axios';
import * as qs from 'qs';
import { fadeInUp } from 'react-animations';
import clearState from './businessLogics/clearState';
import clearStateIfNeeded from './businessLogics/clearStateIfNeeded';
import mergeWithDefaultConfig from './businessLogics/mergeWithDefaultConfig';
import answerToSurvey from './businessLogics/answerToSurvey';
import declineSurvey from './businessLogics/declineSurvey';
import getApiEndpoint from './businessLogics/getApiEndpoint';
import generatePostData from './businessLogics/generatePostData';
import shouldShowToast from './businessLogics/shouldShowToast';
import Alert from './subComponents/Alert';
import Toast from './subComponents/Toast';
import ScoreToast from './subComponents/ScoreToast';
import SurveyForm from './subComponents/SurveyForm';
import { BreakPoint } from './styles/constants';
import sendRAT, {
  ACTION,
  getScreenNameByToastState,
} from './businessLogics/sendRAT';
import {
  TOAST_STATE_ASK,
  TOAST_STATE_NPS,
  TOAST_STATE_FORM,
  TOAST_STATE_FORM_AGREE,
  TOAST_STATE_FORM_DISAGREE,
  TOAST_STATE_COMPLETED,
  TOAST_STATE_DECLINED,
  TOAST_STATE_SHOWN,
} from './states.js';
import sendFreeAnswersToRAT from './businessLogics/sendFreeAnswersToRAT';
import constants from './businessLogics/constants';

const isDesktopScreen = () =>
  window.matchMedia(`(min-width:${BreakPoint.desktop})`).matches;

const shouldStoreState = () =>
  /rexsurvey_save_state=false/.test(window.location.search) === false;

const fadeInUpAnimation = keyframes`${fadeInUp}`;

const AnimatedSurveyForm = styled(SurveyForm)`
  animation: 0.8s ${fadeInUpAnimation};
`;

const AnimatedAlert = styled(Alert)`
  animation: 0.8s ${fadeInUpAnimation};
`;

const initialToastState = config => {
  if (
    (isDesktopScreen() && config.toast.availableOnDesktop === false) ||
    (isDesktopScreen() === false && config.toast.availableOnMobile === false)
  ) {
    return TOAST_STATE_NPS;
  }
  return TOAST_STATE_ASK;
};

class SurveyToast extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isToastOpen: false,
      toastState: initialToastState(props.config),
      config: mergeWithDefaultConfig(props.config),
      npsSelectedValue: undefined,
      formSelectedValues: [],
      formInputValue: undefined,
    };

    clearStateIfNeeded();
    // TODO: might be better to integrate into one method
    if (!shouldStoreState()) {
      // invalidate stored state if shouldStoreState === false
      clearState();
    }

    this.handleScroll = this.handleScroll.bind(this);
    this.throttledHandleScroll = throttle(this.handleScroll, 100);
    this.handleToastCancel = this.handleToastCancel.bind(this);
    this.handleCheckBoxChange = this.handleCheckBoxChange.bind(this);
    this.handleTextChange = this.handleTextChange.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
  }

  componentDidMount() {
    if (shouldShowToast()) {
      window.addEventListener('scroll', this.throttledHandleScroll);
      this.handleScroll();
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { toastState: prevToastState } = prevState;
    const { isToastOpen, toastState, config, npsSelectedValue } = this.state;
    const cId = config.cId.value;

    const prevScreen = getScreenNameByToastState(prevToastState);
    const screen = getScreenNameByToastState(toastState);
    if (isToastOpen && screen !== undefined) {
      // RAT for appear
      const action = ACTION.APPEAR;
      if (screen !== prevScreen) {
        sendRAT({ cId, screen, action });
      } else if (toastState === initialToastState(config)) {
        if (npsSelectedValue === prevState.npsSelectedValue) {
          sendRAT({ cId, screen, action });
        }
      }
    }
  }

  handleScroll() {
    const { triggerIdName } = this.state.config;
    const el = document.getElementById(triggerIdName);
    if (el) {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const top = el.getBoundingClientRect().top + scrollTop;
      const windowHeight =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        window.screen.height;
      const windowBottom = scrollTop + windowHeight;
      if (this.state.isToastOpen === false && top < windowBottom) {
        this.setState({ isToastOpen: true });
        window.removeEventListener('scroll', this.throttledHandleScroll);
      }
    } else {
      this.setState({ isToastOpen: true });
      window.removeEventListener('scroll', this.throttledHandleScroll);
    }
  }

  handleToastAction(option) {
    const cId = this.state.config.cId.value;
    const { screen, action } = option;

    // RAT
    sendRAT({ cId, screen, action });

    const nextState =
      action === ACTION.AGREE ? TOAST_STATE_NPS : TOAST_STATE_FORM_DISAGREE;
    this.setState({ toastState: nextState });
  }

  handleScoreToastAction(option, value) {
    const cId = this.state.config.cId.value;
    const { screen, action } = option;

    // RAT
    sendRAT({ cId, screen, action });
    this.setState({
      npsSelectedValue: value,
    });
    delay(() => {
      this.setState({
        toastState: TOAST_STATE_FORM_AGREE,
      });
    }, 100);
  }

  handleToastCancel(option, event) {
    const { config } = this.state;
    const cId = config.cId.value;
    const { screen, action } = option;
    event.preventDefault();

    // RAT
    sendRAT({ cId, screen, action });

    if (shouldStoreState()) {
      declineSurvey(config.expirationPeriod);
    }
    this.setState({ toastState: TOAST_STATE_DECLINED });
    delay(this.handleComplete, 3000);
  }

  handleToastClose(option, event) {
    const { config } = this.state;
    const cId = config.cId.value;
    const { screen, action } = option;
    event.preventDefault();

    // RAT
    sendRAT({ cId, screen, action });

    if (shouldStoreState()) {
      declineSurvey(config.expirationPeriod);
    }
    this.handleComplete();
  }

  handleFormAction(option) {
    const {
      config,
      npsSelectedValue,
      formSelectedValues,
      formInputValue,
    } = this.state;
    const {
      surveyId,
      nps: { options: npsOptions = [] },
    } = config;
    const cId = config.cId.value;
    const { screen, action } = option;

    // RAT
    sendRAT({ cId, screen, action });
    const npsValue =
      npsOptions.indexOf(
        npsOptions.find(opt => opt.value === npsSelectedValue)
      ) + 1;
    if (formInputValue) {
      sendFreeAnswersToRAT({ cId, npsValue, formInputValue });
    }

    const finish = () => {
      if (shouldStoreState()) {
        answerToSurvey(config.expirationPeriod);
      }
      this.setState({ toastState: TOAST_STATE_COMPLETED });
      delay(this.handleComplete, 3000);
    };

    let endpoint;
    try {
      endpoint = getApiEndpoint({ surveyId });
    } catch (e) {
      console.warn(e);
    }

    const options = {
      async: true,
      crossDomain: true,
      data: qs.stringify(
        generatePostData(
          config,
          npsSelectedValue,
          formSelectedValues,
          formInputValue
        ),
        { indices: false }
      ),
      method: 'post',
      url: endpoint,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      timeout: constants.TIMEOUT_FOR_AXIOS,
    };
    axios(options)
      .then(response => {
        finish();
      })
      .catch(error => {
        // TODO:
        console.warn(error);
        finish();
      });
  }

  handleCheckBoxChange(val, checked) {
    let newVal = this.state.formSelectedValues;
    if (checked) {
      newVal.push(val);
    } else {
      newVal = newVal.filter(v => v !== val);
    }
    this.setState({ formSelectedValues: newVal });
  }

  handleTextChange(event) {
    this.setState({ formInputValue: event.target.value });
  }

  handleComplete(event) {
    if (event) {
      event.preventDefault();
    }

    this.setState({
      isToastOpen: false,
      toastState: TOAST_STATE_SHOWN,
    });
  }

  render() {
    const {
      isToastOpen,
      toastState,
      config,
      npsSelectedValue,
      formSelectedValues,
      formInputValue,
    } = this.state;

    if (isToastOpen && toastState !== TOAST_STATE_SHOWN) {
      const screen = getScreenNameByToastState(toastState);

      if (toastState === TOAST_STATE_ASK) {
        return (
          <Toast
            message={config.toast.inquiryTxt}
            actionLabel={config.toast.yesBtnTxt}
            closeLabel={config.toast.noBtnTxt}
            onActionClick={this.handleToastAction.bind(this, {
              screen,
              action: ACTION.AGREE,
            })}
            onCancelClick={this.handleToastCancel.bind(this, {
              screen,
              action: ACTION.DISAGREE,
            })}
            onCloseClick={this.handleToastClose.bind(this, {
              screen,
              action: ACTION.CLOSE,
            })}
            isOpen
          />
        );
      } else if (toastState === TOAST_STATE_NPS) {
        return (
          <ScoreToast
            message={config.nps.question.label}
            selectedValue={npsSelectedValue}
            values={config.nps.options.map(o => o.value)}
            onScoreClick={v => {
              this.handleScoreToastAction.bind(
                this,
                {
                  screen,
                  action: ACTION.ANSWER,
                },
                v
              )();
            }}
            onCloseClick={this.handleToastClose.bind(this, {
              screen,
              action: ACTION.CLOSE,
            })}
            minScoreLabel={config.nps.txt0}
            maxScoreLabel={config.nps.txt1}
            isOpen
          />
        );
      } else if (
        toastState === TOAST_STATE_FORM ||
        toastState === TOAST_STATE_FORM_AGREE ||
        toastState === TOAST_STATE_FORM_DISAGREE
      ) {
        return (
          <AnimatedSurveyForm
            config={config}
            onActionClick={this.handleFormAction.bind(this, {
              screen,
              action: ACTION.ANSWER,
            })}
            onCloseClick={this.handleToastClose.bind(this, {
              screen,
              action: ACTION.CLOSE,
            })}
            onCheckBoxChange={this.handleCheckBoxChange}
            selectedValues={formSelectedValues}
            textAreaValue={formInputValue}
            onTextAreaChange={this.handleTextChange}
          />
        );
      } else if (toastState === TOAST_STATE_DECLINED) {
        return (
          <AnimatedAlert
            title={config.toast.thanksTxt0}
            message=""
            onCloseClick={this.handleComplete}
            isOpen
          />
        );
      } else if (toastState === TOAST_STATE_COMPLETED) {
        return (
          <AnimatedAlert
            title={config.toast.thanksTxt0}
            message={config.toast.thanksTxt1}
            onCloseClick={this.handleComplete}
            isOpen
          />
        );
      }
    }

    return null;
  }
}

SurveyToast.propTypes = {
  config: PropTypes.shape({
    surveyId: PropTypes.string,
    cId: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    triggerIdName: PropTypes.string,
    expirationPeriod: PropTypes.number,
    toast: PropTypes.shape({
      inquiryTxt: PropTypes.string,
      yesBtnTxt: PropTypes.string,
      noBtnTxt: PropTypes.string,
      thanksTxt0: PropTypes.string,
      thanksTxt1: PropTypes.string,
    }),
    nps: PropTypes.shape({
      question: PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
      }),
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      txt0: PropTypes.string,
      txt1: PropTypes.string,
    }),
    inquiry: PropTypes.shape({
      question: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          label: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      freeInput: PropTypes.shape({
        available: PropTypes.bool,
        name: PropTypes.string,
        title: PropTypes.string,
      }),
      sendBtnTxt: PropTypes.string,
    }),
  }).isRequired,
};

export default SurveyToast;



// WEBPACK FOOTER //
// ./src/components/SurveyToast/index.jsx